import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { Lightning, BatteryCharging, Plug, Leaf } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function EVManagement() {
  const depotMap = useDepots();
  const [buses, setBuses] = useState([]);
  useEffect(() => { api.get("/fleet/buses").then(({data})=> setBuses(data)); }, []);
  const ev = useMemo(() => buses.filter(b => b.bus_type === "EV"), [buses]);
  const charging = useMemo(() => buses.filter(b => b.status === "Charging"), [buses]);
  const avgSoc = ev.length ? Math.round(ev.reduce((s,b)=>s+b.soc,0)/ev.length) : 0;

  const KPIS = [
    { l: "Total EVs", v: ev.length, c: "#22C55E", i: Lightning },
    { l: "Charging", v: charging.length, c: "#06B6D4", i: BatteryCharging },
    { l: "Available EVs", v: ev.filter(b=>b.status==="Available").length, c: "#1E3A8A", i: Plug },
    { l: "Avg SOC", v: avgSoc+"%", c: "#8B5CF6", i: Leaf },
  ];

  const socBuckets = ["0-20","21-40","41-60","61-80","81-100"].map(rng => ({
    range: rng, count: ev.filter(b => {
      const [lo,hi] = rng.split("-").map(Number);
      return b.soc>=lo && b.soc<=hi;
    }).length,
  }));

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div>
        <div className="label-cap text-slate-500">Electric Fleet</div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">EV Management</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {KPIS.map(k => (
          <div key={k.l} className="ktcl-card ktcl-card-hover p-4">
            <div className="flex items-center justify-between">
              <div className="label-cap text-slate-500">{k.l}</div>
              <k.i size={18} weight="duotone" style={{color:k.c}}/>
            </div>
            <div className="font-display text-2xl font-bold mt-2" style={{color:k.c}}>{k.v}</div>
          </div>
        ))}
      </div>
      <div className="grid xl:grid-cols-2 gap-4">
        <div className="ktcl-card p-4">
          <div className="label-cap text-slate-500">Charge Distribution</div>
          <h3 className="font-display font-bold text-lg mb-2">State of Charge buckets</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={socBuckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false}/>
              <XAxis dataKey="range" tick={{fontSize:11}} stroke="#94A3B8"/>
              <YAxis tick={{fontSize:11}} stroke="#94A3B8"/>
              <Tooltip contentStyle={{fontSize:12, borderRadius:6}}/>
              <Bar dataKey="count" fill="#06B6D4" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="ktcl-card overflow-x-auto scroll-thin">
          <div className="p-4 border-b border-slate-200">
            <div className="label-cap text-slate-500">Charging Stations</div>
            <h3 className="font-display font-bold text-lg">Active sessions</h3>
          </div>
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
              {"Fleet ID,Depot,SOC,Charger,Status".split(",").map(h => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {charging.map(b => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono font-bold text-[#1E3A8A]">{b.fleet_id}</td>
                  <td className="px-3 py-2">{fmtDepot(b.depot, depotMap)}</td>
                  <td className="px-3 py-2">{b.soc}%</td>
                  <td className="px-3 py-2 text-xs">DC Fast</td>
                  <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded">Charging</span></td>
                </tr>
              ))}
              {!charging.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No active charging sessions</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
